'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { getToolAuthToken } from '@/lib/browser-tool-usage';

type OutputFormat = 'png' | 'jpeg' | 'webp';

type LoadedImage = {
  element: HTMLImageElement;
  name: string;
  type: string;
  size: number;
  width: number;
  height: number;
};

const outputOptions: Array<{
  value: OutputFormat;
  label: string;
  mimeType: string;
  extension: string;
  qualityEnabled: boolean;
}> = [
  { value: 'png', label: 'PNG', mimeType: 'image/png', extension: 'png', qualityEnabled: false },
  { value: 'jpeg', label: 'JPG', mimeType: 'image/jpeg', extension: 'jpg', qualityEnabled: true },
  { value: 'webp', label: 'WebP', mimeType: 'image/webp', extension: 'webp', qualityEnabled: true },
];

const quickRoutes = [
  { id: 'jpg-to-jpg', label: 'JPG → clean JPG', source: 'JPG', target: 'jpeg' as OutputFormat },
  { id: 'png-to-png', label: 'PNG → clean PNG', source: 'PNG', target: 'png' as OutputFormat },
  {
    id: 'webp-to-webp',
    label: 'WebP → clean WebP',
    source: 'WebP',
    target: 'webp' as OutputFormat,
  },
  { id: 'jpg-to-png', label: 'JPG → PNG', source: 'JPG', target: 'png' as OutputFormat },
  { id: 'webp-to-png', label: 'WebP → PNG', source: 'WebP', target: 'png' as OutputFormat },
];

function formatFileSize(size: number) {
  if (size < 1024) {
    return `${size} B`;
  }

  if (size < 1024 * 1024) {
    return `${(size / 1024).toFixed(1)} KB`;
  }

  return `${(size / (1024 * 1024)).toFixed(2)} MB`;
}

function getBaseName(fileName: string) {
  const lastDot = fileName.lastIndexOf('.');
  return lastDot > 0 ? fileName.slice(0, lastDot) : fileName;
}

function normalizeSourceLabel(type: string | null) {
  if (!type) {
    return 'Auto';
  }

  const label = type.replace('image/', '').toUpperCase();
  return label === 'JPEG' ? 'JPG' : label;
}

async function loadImageFromFile(file: File) {
  const objectUrl = URL.createObjectURL(file);

  try {
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const nextImage = new Image();
      nextImage.onload = () => resolve(nextImage);
      nextImage.onerror = () =>
        reject(new Error('This image format is not supported in your browser yet.'));
      nextImage.src = objectUrl;
    });

    return image;
  } finally {
    URL.revokeObjectURL(objectUrl);
  }
}

export default function BrowserMetadataRemover() {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [loadedImage, setLoadedImage] = useState<LoadedImage | null>(null);
  const [outputFormat, setOutputFormat] = useState<OutputFormat>('png');
  const [quality, setQuality] = useState(0.9);
  const [activeRouteId, setActiveRouteId] = useState<string>('png-to-png');
  const [isDragActive, setIsDragActive] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [creditMessage, setCreditMessage] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [downloadName, setDownloadName] = useState<string | null>(null);
  const [resultSize, setResultSize] = useState<number | null>(null);

  const selectedOutput = useMemo(
    () => outputOptions.find((option) => option.value === outputFormat) ?? outputOptions[0],
    [outputFormat],
  );

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }

      if (downloadUrl) {
        URL.revokeObjectURL(downloadUrl);
      }
    };
  }, [downloadUrl, previewUrl]);

  function resetOutputState() {
    if (previewUrl) {
      URL.revokeObjectURL(previewUrl);
    }

    if (downloadUrl) {
      URL.revokeObjectURL(downloadUrl);
    }

    setPreviewUrl(null);
    setDownloadUrl(null);
    setDownloadName(null);
    setResultSize(null);
  }

  async function handleFileSelection(file: File | null) {
    resetOutputState();
    setErrorMessage(null);
    setCreditMessage(null);

    if (!file) {
      setLoadedImage(null);
      return;
    }

    try {
      const image = await loadImageFromFile(file);
      const sourceLabel = normalizeSourceLabel(file.type);
      const matchingRoute =
        quickRoutes.find((route) => route.source === sourceLabel) ??
        quickRoutes.find((route) => route.target === 'png') ??
        quickRoutes[0];

      setLoadedImage({
        element: image,
        name: file.name,
        type: file.type,
        size: file.size,
        width: image.naturalWidth,
        height: image.naturalHeight,
      });
      setActiveRouteId(matchingRoute.id);
      setOutputFormat(matchingRoute.target);
    } catch (error) {
      setLoadedImage(null);
      setErrorMessage(error instanceof Error ? error.message : 'Could not read this image.');
    }
  }

  function applyRoute(routeId: string) {
    const nextRoute = quickRoutes.find((route) => route.id === routeId);

    if (!nextRoute) {
      return;
    }

    setActiveRouteId(nextRoute.id);
    setOutputFormat(nextRoute.target);
    resetOutputState();
  }

  async function runMetadataRemoval() {
    if (!loadedImage) {
      setErrorMessage('Choose an image first.');
      return;
    }

    setIsProcessing(true);
    setErrorMessage(null);
    setCreditMessage(null);

    try {
      const headers: HeadersInit = {
        'Content-Type': 'application/json',
      };
      const idToken = await getToolAuthToken();

      if (idToken) {
        headers.authorization = `Bearer ${idToken}`;
      }

      const usageResponse = await fetch('/api/tools/usage', {
        method: 'POST',
        headers,
        body: JSON.stringify({ toolSlug: 'metadata-removal' }),
      });
      const usagePayload = (await usageResponse.json().catch(() => ({}))) as {
        success?: boolean;
        data?: {
          credits?: {
            plan?: 'guest' | 'free' | 'pro';
            remaining?: number | null;
            windowLabel?: string;
          };
        };
        error?: unknown;
      };

      if (!usageResponse.ok || !usagePayload.success) {
        const message =
          typeof usagePayload.error === 'object' &&
          usagePayload.error &&
          'message' in usagePayload.error
            ? String((usagePayload.error as { message?: unknown }).message)
            : typeof usagePayload.error === 'string'
              ? usagePayload.error
              : 'Credits are not available for another cleanup right now.';
        throw new Error(message);
      }

      const canvas = document.createElement('canvas');
      canvas.width = loadedImage.width;
      canvas.height = loadedImage.height;

      const context = canvas.getContext('2d');

      if (!context) {
        throw new Error('Your browser could not prepare the image.');
      }

      context.drawImage(loadedImage.element, 0, 0, loadedImage.width, loadedImage.height);

      const blob = await new Promise<Blob | null>((resolve) => {
        canvas.toBlob(
          (nextBlob) => resolve(nextBlob),
          selectedOutput.mimeType,
          selectedOutput.qualityEnabled ? quality : undefined,
        );
      });

      if (!blob) {
        throw new Error('The cleaned file could not be created for this image.');
      }

      const nextPreviewUrl = URL.createObjectURL(blob);
      const nextDownloadUrl = URL.createObjectURL(blob);
      const nextFileName = `${getBaseName(loadedImage.name)}-clean.${selectedOutput.extension}`;

      setPreviewUrl(nextPreviewUrl);
      setDownloadUrl(nextDownloadUrl);
      setDownloadName(nextFileName);
      setResultSize(blob.size);
      setCreditMessage(
        usagePayload.data?.credits?.plan === 'pro'
          ? 'Unlimited plan active.'
          : usagePayload.data?.credits?.remaining !== undefined
            ? `${usagePayload.data.credits.remaining} credits left in this ${usagePayload.data.credits.windowLabel?.toLowerCase() || 'current'} window.`
            : null,
      );
    } catch (error) {
      setErrorMessage(
        error instanceof Error ? error.message : 'The metadata cleanup could not finish.',
      );
    } finally {
      setIsProcessing(false);
    }
  }

  return (
    <article className="tool-live-card">
      <div className="tool-live-header">
        <div>
          <h2>Remove image metadata in the browser</h2>
          <p>
            Re-export one image without the common EXIF and device details that often travel with
            the original file.
          </p>
        </div>
        <span className="tool-live-badge">Browser-first</span>
      </div>

      <div className="tool-live-routes" aria-label="Popular metadata cleanup routes">
        {quickRoutes.map((route) => (
          <button
            key={route.id}
            className={`tool-live-route ${activeRouteId === route.id ? 'tool-live-route-active' : ''}`}
            onClick={() => applyRoute(route.id)}
            type="button"
          >
            {route.label}
          </button>
        ))}
      </div>

      <div className="tool-live-grid">
        <div className="tool-live-uploader">
          <button
            className={`tool-live-dropzone ${isDragActive ? 'tool-live-dropzone-active' : ''}`}
            onClick={() => inputRef.current?.click()}
            onDragEnter={() => setIsDragActive(true)}
            onDragLeave={() => setIsDragActive(false)}
            onDragOver={(event) => {
              event.preventDefault();
              setIsDragActive(true);
            }}
            onDrop={(event) => {
              event.preventDefault();
              setIsDragActive(false);
              void handleFileSelection(event.dataTransfer.files?.[0] ?? null);
            }}
            type="button"
          >
            <div>
              <strong>{loadedImage ? loadedImage.name : 'Choose an image or drop it here'}</strong>
              <span>
                {loadedImage
                  ? `${formatFileSize(loadedImage.size)} • ${loadedImage.type || 'Image file'}`
                  : 'Works best with PNG, JPG, JPEG, WebP, GIF, and BMP files'}
              </span>
            </div>
          </button>
          <input
            ref={inputRef}
            accept="image/*"
            className="tool-live-file-input"
            onChange={(event) => void handleFileSelection(event.target.files?.[0] ?? null)}
            type="file"
          />

          <div className="tool-live-controls">
            <label className="field">
              <span>From</span>
              <input
                disabled
                readOnly
                type="text"
                value={
                  loadedImage ? normalizeSourceLabel(loadedImage.type) : 'Auto-detect after upload'
                }
              />
            </label>
            <label className="field">
              <span>Export as</span>
              <select
                onChange={(event) => {
                  setActiveRouteId('custom');
                  setOutputFormat(event.target.value as OutputFormat);
                  resetOutputState();
                }}
                value={outputFormat}
              >
                {outputOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </label>
          </div>

          <div className="tool-live-quality-shell">
            <label
              className={`field tool-live-quality ${selectedOutput.qualityEnabled ? '' : 'tool-live-quality-disabled'}`}
            >
              <span>Quality</span>
              <input
                disabled={!selectedOutput.qualityEnabled}
                max="0.95"
                min="0.5"
                onChange={(event) => {
                  setActiveRouteId('custom');
                  setQuality(Number(event.target.value));
                  resetOutputState();
                }}
                step="0.01"
                type="range"
                value={quality}
              />
              <small>
                {selectedOutput.qualityEnabled
                  ? `${Math.round(quality * 100)}% quality`
                  : 'PNG keeps a clean export without a quality slider'}
              </small>
            </label>
          </div>

          <div className="hero-actions tool-live-actions">
            <button
              className="button"
              disabled={!loadedImage || isProcessing}
              onClick={() => void runMetadataRemoval()}
              type="button"
            >
              {isProcessing ? 'Cleaning…' : `Clean and export as ${selectedOutput.label}`}
            </button>
            {downloadUrl && downloadName ? (
              <a className="button button-ghost" download={downloadName} href={downloadUrl}>
                Download {selectedOutput.label}
              </a>
            ) : null}
          </div>

          {creditMessage ? (
            <p className="support-feedback support-feedback-success">{creditMessage}</p>
          ) : null}
          {errorMessage ? (
            <p className="support-feedback support-feedback-error">{errorMessage}</p>
          ) : null}
        </div>

        <aside className="tool-live-panel">
          <h3>Cleaner file, same image</h3>
          <div className="tool-mockup-chip-list">
            <span>EXIF</span>
            <span>Device details</span>
            <span>Location data</span>
          </div>
          <p>
            Use this before sharing, uploading, or publishing an image when you want the visible
            picture but not the extra file details around it.
          </p>
          <div className="tool-live-panel-note">
            <strong>Best browser support</strong>
            <span>Upload PNG, JPG, JPEG, WebP, GIF, or BMP. Export to PNG, JPG, or WebP.</span>
          </div>
          {loadedImage ? (
            <div className="tool-live-stats">
              <div>
                <strong>Source</strong>
                <span>{formatFileSize(loadedImage.size)}</span>
              </div>
              <div>
                <strong>Type</strong>
                <span>{normalizeSourceLabel(loadedImage.type)}</span>
              </div>
              <div>
                <strong>Resolution</strong>
                <span>{`${loadedImage.width} × ${loadedImage.height}`}</span>
              </div>
              <div>
                <strong>Export</strong>
                <span>{selectedOutput.label}</span>
              </div>
              {resultSize ? (
                <div>
                  <strong>Clean file</strong>
                  <span>{formatFileSize(resultSize)}</span>
                </div>
              ) : null}
              <div>
                <strong>Privacy</strong>
                <span>Processed in your browser</span>
              </div>
            </div>
          ) : null}
        </aside>
      </div>

      {loadedImage ? (
        <div className="tool-live-preview tool-compression-preview">
          <h3>Preview</h3>
          <div className="tool-compression-preview-grid">
            <div className="tool-live-preview-frame">
              <img alt="Original preview" src={loadedImage.element.src} />
            </div>
            <div className="tool-live-preview-frame">
              {previewUrl ? (
                <img alt="Cleaned preview" src={previewUrl} />
              ) : (
                <div className="tool-editor-empty">
                  <strong>Cleaned preview</strong>
                  <span>Run the cleanup to prepare the export.</span>
                </div>
              )}
            </div>
          </div>
          {resultSize ? <p>Clean export size: {formatFileSize(resultSize)}</p> : null}
        </div>
      ) : null}
    </article>
  );
}
